import { FormLabel } from 'components';
import { FormErrorMessage } from 'components/form/FormErrorMessage';
import { InputGroup } from 'components/form/InputGroup';
import { Select } from 'components/form/Select';
import { Flex } from 'components/layout/Flex';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { lookupState } from 'state/atoms';
import { DIFFERENT_ACCOUNT_STATUS } from 'utils/constants';

import {
  TextArea,
  FailureDescriptionWrapper,
  ReviewRequestedLabel,
  CancelReviewAction,
  RemainingCharWrapper,
  ReviewCounter,
  SelectField,
} from './styledComponents';

const VALIDATION_STATE = {
  failureCode: { isValid: true },
  failureDescription: { isValid: true },
  serviceActivity: { isValid: true },
  reviewDescription: { isValid: true },
};

const DESCRIPTION_MAX_VALUE = 1999;
const REVIEW_MAX_VALUE = 255;
const REVIEW_LABEL = 'reviewDescription';
const DESCRIPTION_LABEL = 'failureDescription';

const RmaFiels = () => {
  const [currentLookupState, setLookupState] = useRecoilState(lookupState);
  const [reviewDescriptionCount, setReviewDescriptionCount] = useState(0);
  const [fieldValidation, setFieldValidation] = useState(VALIDATION_STATE);
  const [descriptionLength, setDescriptionLength] = useState(DESCRIPTION_MAX_VALUE);
  const [reviewLength, setReviewLength] = useState(REVIEW_MAX_VALUE);

  const setLookupFieldData = useCallback(
    (property, value) => {
      setLookupState({
        ...currentLookupState,
        [property]: {
          touched: true,
          value,
        },
      });
    },
    [currentLookupState]
  );

  const cancelReview = useCallback(
    (event) => {
      event.preventDefault();

      setReviewDescriptionCount(0);

      setLookupState({
        ...currentLookupState,
        reviewRequested: false,
        reviewDescription: {
          touched: false,
          value: '',
        },
      });
    },
    [currentLookupState, setLookupState]
  );

  /**
   * Control whether the customer can cancel the review
   */
  const canCancelReview = useCallback(() => {
    return currentLookupState.selectedResult.status !== DIFFERENT_ACCOUNT_STATUS;
  }, [currentLookupState]);

  useEffect(() => {
    const failureLength = currentLookupState.failureDescription.value.length;
    const reviewLength = currentLookupState.reviewDescription.value.length;
    const validationResult = { ...fieldValidation };

    Object.keys(fieldValidation).forEach((validatableField) => {
      const { touched, value } = currentLookupState[validatableField];
      validationResult[validatableField].isValid = value.length || !touched;

      const isReviewField = validatableField === REVIEW_LABEL;
      if (isReviewField && touched && value.length < 19) {
        validationResult[validatableField].isValid = false;
      }
    });

    setDescriptionLength(DESCRIPTION_MAX_VALUE - failureLength);
    setReviewLength(REVIEW_MAX_VALUE - reviewLength);
    setFieldValidation(validationResult);
  }, [currentLookupState]);

  const handleMaxLength = (length, id) => {
    const isReviewDescription = id === REVIEW_LABEL;

    if (isReviewDescription) {
      setReviewLength(REVIEW_MAX_VALUE - length);
    } else {
      setDescriptionLength(DESCRIPTION_MAX_VALUE - length);
    }
  };

  return (
    <Flex>
      <SelectField>
        <InputGroup>
          <FormLabel htmlFor="failureCode">Failure Code</FormLabel>
          <Select
            id="failureCode"
            hasError={!fieldValidation.failureCode.isValid}
            value={currentLookupState.failureCode.value || ''}
            onChange={(event) => setLookupFieldData('failureCode', event.currentTarget.value)}
          >
            <option value="">Select</option>
            <option value="Card Failure">Card Failure</option>
            <option value="Boot Failure">Boot Failure</option>
            <option value="Degraded Performance">Degraded Performance</option>
            <option value="Port Failure">Port Failure</option>
            <option value="Software Failure">Software Failure</option>
            <option value="ONT Rogue Detected">ONT Rogue Detected</option>
            <option value="AE ONT Dropped">AE ONT Dropped</option>
          </Select>
          {!fieldValidation.failureCode.isValid ? (
            <FormErrorMessage>Failure Code is mandatory</FormErrorMessage>
          ) : null}
        </InputGroup>
        <InputGroup>
          <FormLabel htmlFor="serviceActivity">Service Activity</FormLabel>
          <Select
            id="serviceActivity"
            hasError={!fieldValidation.serviceActivity.isValid}
            value={currentLookupState.serviceActivity.value || ''}
            onChange={(event) => setLookupFieldData('serviceActivity', event.currentTarget.value)}
          >
            <option value="">Select</option>
            <option value="In Service Failure">In Service Failure</option>
            <option value="Out of Box Failure">Out of Box Failure</option>
          </Select>
          {!fieldValidation.serviceActivity.isValid ? (
            <FormErrorMessage>Service Activity is mandatory</FormErrorMessage>
          ) : null}
        </InputGroup>
      </SelectField>

      <FailureDescriptionWrapper>
        <Flex direction="column">
          <InputGroup>
            <FormLabel htmlFor={DESCRIPTION_LABEL}>Description Of Failure</FormLabel>
            <TextArea
              id={DESCRIPTION_LABEL}
              hasError={!fieldValidation.failureDescription.isValid}
              rows="6"
              maxLength={DESCRIPTION_MAX_VALUE}
              defaultValue={currentLookupState.failureDescription.value || ''}
              onBlur={(event) => setLookupFieldData(DESCRIPTION_LABEL, event.currentTarget.value)}
              onChange={(e) => handleMaxLength(e.target.value.length, DESCRIPTION_LABEL)}
            ></TextArea>
            {!fieldValidation.failureDescription.isValid ? (
              <FormErrorMessage>Failure Description is mandatory</FormErrorMessage>
            ) : null}
            <RemainingCharWrapper>Remaining Characters: {descriptionLength}</RemainingCharWrapper>
          </InputGroup>
          {currentLookupState.reviewRequested ? (
            <InputGroup>
              <ReviewRequestedLabel htmlFor={REVIEW_LABEL}>
                Please include any details you have pertaining your review request of this item’s
                warranty coverage status.
              </ReviewRequestedLabel>
              <TextArea
                id={REVIEW_LABEL}
                rows="6"
                maxLength={REVIEW_MAX_VALUE}
                hasError={!fieldValidation.reviewDescription.isValid}
                defaultValue={currentLookupState.reviewDescription.value || ''}
                onChange={(event) => {
                  setReviewDescriptionCount(event.currentTarget.value.length);
                  handleMaxLength(event.target.value.length, REVIEW_LABEL);
                }}
                onBlur={(event) => setLookupFieldData(REVIEW_LABEL, event.currentTarget.value)}
              ></TextArea>
              <Flex spacing="space-between">
                <RemainingCharWrapper>Remaining Characters: {reviewLength}</RemainingCharWrapper>
                <div>
                  {canCancelReview() ? (
                    <CancelReviewAction href="#" onClick={cancelReview}>
                      Cancel Review Request
                    </CancelReviewAction>
                  ) : null}
                </div>
                {reviewDescriptionCount <= 19 && (
                  <ReviewCounter>
                    Minimum Character Count: {-20 + reviewDescriptionCount}
                  </ReviewCounter>
                )}
              </Flex>
            </InputGroup>
          ) : null}
        </Flex>
      </FailureDescriptionWrapper>
    </Flex>
  );
};

export default RmaFiels;
